import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Tag, Space, message, Popconfirm, Select, Tabs, Switch, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useMenuPermission } from '../hooks/useMenuPermission';

const { TabPane } = Tabs;

// 操作权限字段
const OPERATION_FIELDS = [
  { key: 'allow_add', label: '新增订单' },
  { key: 'allow_delete', label: '删除订单' },
  { key: 'allow_import', label: '批量导入' },
  { key: 'allow_quick_add', label: '快速导入' },
  { key: 'allow_batch_assign', label: '批量分配' },
  { key: 'allow_batch_edit', label: '批量修改' }
];

function SupplierManage() {
  const navigate = useNavigate();
  const { canEdit } = useMenuPermission();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [form] = Form.useForm();

  // 权限配置相关状态
  const [permModalVisible, setPermModalVisible] = useState(false);
  const [permSupplier, setPermSupplier] = useState(null);
  const [permLoading, setPermLoading] = useState(false);
  const [permSaving, setPermSaving] = useState(false);
  const [operations, setOperations] = useState({});
  const [menuPermissions, setMenuPermissions] = useState([]);
  const [fieldPermissions, setFieldPermissions] = useState([]);
  const [supplierTemplates, setSupplierTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [dataPermissions, setDataPermissions] = useState({ platforms: [], teams: [], statuses: [] });
  const [allPlatforms, setAllPlatforms] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [allStatuses, setAllStatuses] = useState([]);

  // 账单权限
  const [billPermissions, setBillPermissions] = useState([
    { field_name: 'bill_team', label: '小组' },
    { field_name: 'bill_settlement_date', label: '结算日期' },
    { field_name: 'bill_amount', label: '金额' },
    { field_name: 'bill_is_paid', label: '是否打款' },
    { field_name: 'bill_is_invoiced', label: '是否开票' },
    { field_name: 'bill_is_collected', label: '是否回单' },
    { field_name: 'bill_supplier_id', label: '主体' },
    { field_name: 'bill_payee_supplier', label: '打款主体' },
    { field_name: 'bill_pay_supplier', label: '收款主体' },
    { field_name: 'bill_invoice_file', label: '发票上传' },
    { field_name: 'bill_remark', label: '备注' }
  ].map(f => ({ ...f, is_visible: 1, is_editable: 1 })));

  useEffect(() => {
    loadData();
  }, [page]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/suppliers', { params: { page, pageSize: 100 } });
      if (res.code === 200) {
        setData(res.data.list);
        setTotal(res.data.total);
      }
    } catch (err) {}
    setLoading(false);
  };

  const handleAdd = () => {
    setEditingSupplier(null);
    form.resetFields();
    form.setFieldsValue({ status: 1 });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingSupplier(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      let res;
      if (editingSupplier) {
        res = await api.put(`/suppliers/${editingSupplier.id}`, values);
      } else {
        res = await api.post('/suppliers', values);
      }
      if (res.code === 200) {
        message.success(editingSupplier ? '更新成功' : '创建成功');
        setModalVisible(false);
        loadData();
      }
    } catch (err) {}
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/suppliers/${id}`);
      if (res.code === 200) {
        message.success('删除成功');
        loadData();
      }
    } catch (err) {}
  };

  // ====== 权限配置 ======

  const openPermModal = async (record) => {
    setPermSupplier(record);
    setPermModalVisible(true);
    setPermLoading(true);
    setSelectedTemplateId(record.template_id || null);

    try {
      // 加载主体模板列表、主体权限、平台和小组列表
      const [templateRes, permRes, platformsRes, teamsRes, statusRes] = await Promise.all([
        api.get('/permission-templates', { params: { type: 'supplier' } }),
        api.get(`/suppliers/${record.id}/permissions`),
        api.get('/platforms'),
        api.get('/teams'),
        api.get('/statuses')
      ]);

      if (templateRes.code === 200) {
        setSupplierTemplates(templateRes.data || []);
      }

      if (permRes.code === 200) {
        const { operations: ops, menus, fields, platforms, teams, statuses, bill_fields } = permRes.data;
        setOperations(ops || {});
        setMenuPermissions(menus || []);
        setFieldPermissions(fields || []);
        setDataPermissions({ platforms: platforms || [], teams: teams || [], statuses: statuses || [] });
        if (bill_fields?.length > 0) {
          setBillPermissions(prev => prev.map(p => {
            const saved = bill_fields.find(f => f.field_name === p.field_name);
            return saved ? { ...p, is_visible: saved.is_visible, is_editable: saved.is_editable } : p;
          }));
        }
      }

      if (platformsRes.code === 200) setAllPlatforms(platformsRes.data || []);
      if (teamsRes.code === 200) setAllTeams(teamsRes.data || []);
      if (statusRes.code === 200) setAllStatuses(statusRes.data || []);
    } catch (err) {
      message.error('加载权限数据失败');
    }
    setPermLoading(false);
  };

  const handleTemplateSelect = async (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;

    try {
      const res = await api.get(`/permission-templates/${templateId}`);
      if (res.code === 200) {
        const tpl = res.data;
        setOperations({
          allow_add: tpl.allow_add,
          allow_delete: tpl.allow_delete,
          allow_import: tpl.allow_import,
          allow_quick_add: tpl.allow_quick_add,
          allow_batch_assign: tpl.allow_batch_assign,
          allow_batch_edit: tpl.allow_batch_edit
        });
        if (tpl.menus) setMenuPermissions(tpl.menus.map(m => ({
          menu_key: m.key,
          label: m.label,
          is_visible: m.is_visible,
          is_editable: m.is_editable
        })));
        if (tpl.fields) setFieldPermissions(tpl.fields.map(f => ({
          field_name: f.field_name,
          label: f.field_name,
          is_visible: f.is_visible,
          is_editable: f.is_editable
        })));
        if (tpl.platforms || tpl.teams || tpl.statuses) {
          setDataPermissions({
            platforms: tpl.platforms || [],
            teams: tpl.teams || [],
            statuses: tpl.statuses || []
          });
        }
        message.success('已加载模板配置');
      }
    } catch (err) {
      message.error('加载模板失败');
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplateId || !permSupplier) return;
    try {
      const res = await api.post(`/permission-templates/${selectedTemplateId}/apply`, {
        supplier_ids: [permSupplier.id]
      });
      if (res.code === 200) {
        message.success('模板应用成功');
        setPermModalVisible(false);
        loadData();
      }
    } catch (err) {
      message.error('应用模板失败');
    }
  };

  const handlePermSave = async () => {
    if (!permSupplier) return;
    setPermSaving(true);
    try {
      const res = await api.put(`/suppliers/${permSupplier.id}/permissions`, {
        operations,
        menus: menuPermissions.map(m => ({
          menu_key: m.menu_key,
          is_visible: m.is_visible,
          is_editable: m.is_editable
        })),
        fields: fieldPermissions.map(f => ({
          field_name: f.field_name,
          is_visible: f.is_visible,
          is_editable: f.is_editable
        })),
        platforms: dataPermissions.platforms,
        teams: dataPermissions.teams,
        statuses: dataPermissions.statuses,
        bill_fields: billPermissions
      });
      if (res.code === 200) {
        message.success('保存成功');
        setPermModalVisible(false);
        loadData();
      }
    } catch (err) {
      message.error('保存失败');
    }
    setPermSaving(false);
  };

  const handleMenuToggle = (menuKey, key, checked) => {
    setMenuPermissions(prev =>
      prev.map(m => {
        if (m.menu_key !== menuKey) return m;
        const updated = { ...m, [key]: checked };
        if (key === 'is_visible' && !checked) updated.is_editable = false;
        return updated;
      })
    );
  };

  const handleFieldToggle = (fieldName, key, checked) => {
    setFieldPermissions(prev =>
      prev.map(f => {
        if (f.field_name !== fieldName) return f;
        const updated = { ...f, [key]: checked };
        if (key === 'is_visible' && !checked) updated.is_editable = false;
        return updated;
      })
    );
  };

  const handleSelectAllMenus = (key, value) => {
    setMenuPermissions(prev => prev.map(m => {
      const updated = { ...m, [key]: value };
      if (key === 'is_visible' && !value) updated.is_editable = false;
      return updated;
    }));
  };

  const handleSelectAllFields = (key, value) => {
    setFieldPermissions(prev => prev.map(f => {
      const updated = { ...f, [key]: value };
      if (key === 'is_visible' && !value) updated.is_editable = false;
      return updated;
    }));
  };

  const menuColumns = [
    { title: '菜单名称', dataIndex: 'label', width: 180 },
    { title: '菜单标识', dataIndex: 'menu_key', render: v => <Tag>{v}</Tag> },
    {
      title: '是否可见', dataIndex: 'is_visible', width: 100,
      render: (v, record) => (
        <Switch checked={v} size="small" onChange={checked => handleMenuToggle(record.menu_key, 'is_visible', checked)} />
      )
    },
    {
      title: '是否可编辑', dataIndex: 'is_editable', width: 100,
      render: (v, record) => (
        <Switch checked={v} size="small" disabled={!record.is_visible} onChange={checked => handleMenuToggle(record.menu_key, 'is_editable', checked)} />
      )
    }
  ];

  const fieldColumns = [
    { title: '字段名称', dataIndex: 'label', width: 180 },
    { title: '字段标识', dataIndex: 'field_name', render: v => <Tag>{v}</Tag> },
    {
      title: '是否可见', dataIndex: 'is_visible', width: 100,
      render: (v, record) => (
        <Switch checked={v} size="small" onChange={checked => handleFieldToggle(record.field_name, 'is_visible', checked)} />
      )
    },
    {
      title: '是否可编辑', dataIndex: 'is_editable', width: 100,
      render: (v, record) => (
        <Switch checked={v} size="small" disabled={!record.is_visible} onChange={checked => handleFieldToggle(record.field_name, 'is_editable', checked)} />
      )
    }
  ];

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '主体名称', dataIndex: 'name', width: 200 },
    { title: '联系人', dataIndex: 'contact', width: 120, render: v => v || '-' },
    { title: '联系电话', dataIndex: 'phone', width: 150, render: v => v || '-' },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: v => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '启用' : '禁用'}</Tag>
    },
    {
      title: '创建时间', dataIndex: 'created_at', width: 160,
      render: v => v ? new Date(v).toLocaleString() : '-'
    },
    {
      title: '操作', key: 'action', width: 280,
      render: (_, record) => (
        <Space>
          {canEdit && <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>}
          {canEdit && <Button type="link" size="small" icon={<SettingOutlined />} onClick={() => openPermModal(record)}>权限配置</Button>}
          {canEdit && (
            <Popconfirm title="确定删除？删除前请确保该主体下无订单。" onConfirm={() => handleDelete(record.id)}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h3>主体管理</h3>
        {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增主体</Button>}
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: page,
          pageSize: 100,
          total,
          showTotal: t => `共 ${t} 条`,
          onChange: p => setPage(p)
        }}
      />

      {/* 新增/编辑主体弹窗 */}
      <Modal
        title={editingSupplier ? '编辑主体' : '新增主体'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={500}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="主体名称" rules={[{ required: true, message: '请输入主体名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="contact" label="联系人">
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="联系电话">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* 权限配置弹窗 */}
      <Modal
        title={`权限配置 - ${permSupplier?.name || ''}`}
        open={permModalVisible}
        onCancel={() => setPermModalVisible(false)}
        width={950}
        footer={
          <Space>
            <Button onClick={() => setPermModalVisible(false)}>取消</Button>
            {selectedTemplateId && (
              <Button type="primary" onClick={handleApplyTemplate}>
                应用模板
              </Button>
            )}
            <Button type="primary" loading={permSaving} onClick={handlePermSave}>
              保存配置
            </Button>
          </Space>
        }
      >
        {permLoading ? (
          <Spin size="large" style={{ display: 'block', margin: '60px auto' }} />
               ) : (
          <div>
            {/* 模板选择 */}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontWeight: 500 }}>关联模板：</span>
              <Select
                style={{ width: 300 }}
                placeholder="选择主体模板（可选）"
                allowClear
                value={selectedTemplateId}
                onChange={handleTemplateSelect}
              >
                {supplierTemplates.map(t => (
                  <Select.Option key={t.id} value={t.id}>{t.name}</Select.Option>
                ))}
              </Select>
              {selectedTemplateId && (
                <Button size="small" onClick={handleApplyTemplate}>应用模板</Button>
              )}
            </div>

            {/* 操作权限 */}
            <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fafafa', borderRadius: 6 }}>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>操作权限</div>
              <Space wrap>
                {OPERATION_FIELDS.map(f => (
                  <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>{f.label}：</span>
                    <Select
                      size="small"
                      style={{ width: 80 }}
                      value={operations[f.key] !== undefined ? operations[f.key] : 1}
                      onChange={v => setOperations(prev => ({ ...prev, [f.key]: v }))}
                    >
                      <Select.Option value={1}>允许</Select.Option>
                      <Select.Option value={0}>禁止</Select.Option>
                    </Select>
                  </div>
                ))}
              </Space>
            </div>

            {/* 菜单权限 + 字段权限 Tab */}
            <Tabs defaultActiveKey="menus">
              <TabPane tab="菜单权限" key="menus">
                <div style={{ marginBottom: 8 }}>
                  <Space>
                    <Button size="small" onClick={() => handleSelectAllMenus('is_visible', true)}>全部可见</Button>
                    <Button size="small" onClick={() => handleSelectAllMenus('is_visible', false)}>全部隐藏</Button>
                    <Button size="small" onClick={() => handleSelectAllMenus('is_editable', true)}>全部可编辑</Button>
                    <Button size="small" onClick={() => handleSelectAllMenus('is_editable', false)}>全部只读</Button>
                  </Space>
                </div>
                <Table
                  rowKey="menu_key"
                  columns={menuColumns}
                  dataSource={menuPermissions}
                  pagination={false}
                  size="small"
                  scroll={{ y: 300 }}
                />
              </TabPane>
              <TabPane tab="字段权限" key="fields">
                <div style={{ marginBottom: 8 }}>
                  <Space>
                    <Button size="small" onClick={() => handleSelectAllFields('is_visible', true)}>全部可见</Button>
                    <Button size="small" onClick={() => handleSelectAllFields('is_visible', false)}>全部隐藏</Button>
                    <Button size="small" onClick={() => handleSelectAllFields('is_editable', true)}>全部可编辑</Button>
                    <Button size="small" onClick={() => handleSelectAllFields('is_editable', false)}>全部只读</Button>
                  </Space>
                </div>
                <Table
                  rowKey="field_name"
                  columns={fieldColumns}
                  dataSource={fieldPermissions}
                  pagination={false}
                  size="small"
                  scroll={{ y: 300 }}
                />
              </TabPane>
              <TabPane tab="账单权限" key="bill">
                <Space style={{ marginBottom: 12 }}>
                  <Button size="small" onClick={() => setBillPermissions(prev => prev.map(p => ({ ...p, is_visible: 1 })))}>全部可见</Button>
                  <Button size="small" onClick={() => setBillPermissions(prev => prev.map(p => ({ ...p, is_visible: 0, is_editable: 0 })))}>全部隐藏</Button>
                  <Button size="small" onClick={() => setBillPermissions(prev => prev.map(p => ({ ...p, is_editable: 1 })))}>全部可编辑</Button>
                  <Button size="small" onClick={() => setBillPermissions(prev => prev.map(p => ({ ...p, is_editable: 0 })))}>全部只读</Button>
                </Space>
                <Table rowKey="field_name" size="small" pagination={false}
                  columns={[
                    { title: '字段名', dataIndex: 'field_name' },
                    { title: '中文名', dataIndex: 'label' },
                    { title: '可见', dataIndex: 'is_visible', width: 70, align: 'center',
                      render: (v, r) => <Switch checked={v===1} onChange={c => setBillPermissions(prev => prev.map(p => p.field_name===r.field_name ? {...p, is_visible: c?1:0, is_editable: c?p.is_editable:0} : p))} /> },
                    { title: '可编辑', dataIndex: 'is_editable', width: 70, align: 'center',
                      render: (v, r) => <Switch checked={v===1} onChange={c => setBillPermissions(prev => prev.map(p => p.field_name===r.field_name ? {...p, is_editable: c?1:0} : p))} /> }
                  ]}
                  dataSource={billPermissions}
                />
              </TabPane>
              <TabPane tab="数据权限" key="data">
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 500, marginBottom: 8 }}>允许查看的平台</div>
                  <p style={{ color: '#999', marginBottom: 8 }}>不选择任何平台 = 查看所有平台的订单</p>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="选择允许的平台（留空=全部）"
                    value={dataPermissions.platforms}
                    onChange={v => setDataPermissions(prev => ({ ...prev, platforms: v }))}
                  >
                    <Select.Option key="__blank__" value="__blank__">（空白）</Select.Option>
                    {allPlatforms.map(p => (
                      <Select.Option key={p.name} value={p.name}>{p.name}</Select.Option>
                    ))}
                  </Select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 500, marginBottom: 8 }}>允许查看的小组</div>
                  <p style={{ color: '#999', marginBottom: 8 }}>不选择任何小组 = 查看所有小组的订单</p>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="选择允许的小组（留空=全部）"
                    value={dataPermissions.teams}
                    onChange={v => setDataPermissions(prev => ({ ...prev, teams: v }))}
                  >
                    <Select.Option key="__blank__" value="__blank__">（空白）</Select.Option>
                    {allTeams.map(t => (
                      <Select.Option key={t.name} value={t.name}>{t.name}</Select.Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <div style={{ fontWeight: 500, marginBottom: 8 }}>限制修改的状态</div>
                  <p style={{ color: '#999', marginBottom: 8 }}>选中的状态将禁止该主体修改。状态管理中锁定的状态对所有人限制，优先级最高。</p>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="选择要限制的状态（留空=不限制）"
                    value={dataPermissions.statuses}
                    onChange={v => setDataPermissions(prev => ({ ...prev, statuses: v }))}
                  >
                    {allStatuses.filter(s => !s.is_locked).map(s => (
                      <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
                    ))}
                  </Select>
                </div>
              </TabPane>
            </Tabs>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default SupplierManage;
